/**
 * SHARED ACTIVITY VOCABULARY — client side.
 *
 * Phase 4.3. Message's accepted activity contract is the canonical source of truth for what
 * Arvexis says she is doing. It used to live inside CompanionChat; it lives here now so Talk and
 * Dream can report real work in the same words instead of inventing separate state systems.
 * The server mirror is `backend/activity_events.py` and only ever sends a key — the wording,
 * including Spanish, belongs here.
 *
 * RULES (Founder, 4.3):
 *  - a state appears only when the operation it names is actually running;
 *  - no decorative or timed "thinking" animation, no artificial delay;
 *  - a key is permitted only on surfaces where that operation can genuinely occur;
 *  - absence of an operation means absence of the state;
 *  - on failure the activity layer goes quiet and Arvexis explains it in her own words.
 *
 * Each surface keeps its own rendering and visual identity. Only the vocabulary is shared.
 */

export const ACTIVITY = {
  THINKING: 'thinking',
  SEARCHING: 'searching',
  READING: 'reading',
  LOOKING: 'looking',
  LISTENING_RECORDING: 'listening_recording',
  CHECKING_LINK: 'checking_link',
  REVIEWING_VIDEO: 'reviewing_video',
  LISTENING_VIDEO: 'listening_video',
  WATCHING_VIDEO: 'watching_video',
  UNDERSTANDING_VIDEO: 'understanding_video',
  CREATING_IMAGE: 'creating_image',
  CONNECTING: 'connecting',
  RECALLING_DREAM: 'recalling_dream',
  ANALYZING_DREAM: 'analyzing_dream',
  TRANSCRIBING: 'transcribing',
  DEFAULT: 'default',
};

export const ACTIVITY_COPY = {
  // 4.4 — this appears ONLY when the deep-reasoning path actually runs (verified arithmetic
  // and/or a raised reasoning budget). Never for an ordinary reply.
  thinking: { en: 'Working it through...', es: 'Analizándolo con calma...' },
  searching: { en: 'Looking up current information...', es: 'Buscando información actual...' },
  reading: { en: 'Reading your document...', es: 'Leyendo tu documento...' },
  looking: { en: 'Looking at your image...', es: 'Mirando tu imagen...' },
  listening_recording: { en: 'Listening to your recording...', es: 'Escuchando tu grabación...' },
  checking_link: { en: 'Checking this link...', es: 'Revisando este enlace...' },
  reviewing_video: { en: 'Reviewing your video...', es: 'Revisando tu video...' },
  listening_video: { en: "Listening to what's said...", es: 'Escuchando lo que se dice...' },
  watching_video: { en: 'Watching what happens on screen...', es: 'Viendo lo que ocurre en pantalla...' },
  understanding_video: { en: 'Putting it together...', es: 'Uniendo todo...' },
  creating_image: { en: 'Creating your image...', es: 'Creando tu imagen...' },
  connecting: { en: 'Connecting with previous conversations...', es: 'Conectando con conversaciones anteriores...' },
  recalling_dream: { en: 'Looking back at your dream...', es: 'Revisando tu sueño...' },
  analyzing_dream: { en: 'Exploring the patterns in your dream...', es: 'Explorando los patrones en tu sueño...' },
  transcribing: { en: 'Transcribing...', es: 'Transcribiendo...' },
  listening: { en: 'Listening...', es: 'Escuchando...' },
  default: { en: 'Present...', es: 'Presente...' },
};

/**
 * Where each operation can actually happen. Permission is not a promise that the state will
 * appear: every appearance still requires a real runtime event on that surface.
 */
export const ACTIVITY_SURFACES = {
  // Ordinary response generation is expected conversation, not a capability worth announcing
  // (4.3 acceptance delta). 4.4 made the state real: it is emitted only when the deep-reasoning
  // path actually runs — exact arithmetic and/or a raised reasoning budget.
  thinking: ['message', 'talk'],
  searching: ['message', 'talk'],
  reading: ['message'],
  looking: ['message'],
  listening_recording: ['message'],
  checking_link: ['message'],
  reviewing_video: ['message'],
  listening_video: ['message'],
  watching_video: ['message'],
  understanding_video: ['message'],
  creating_image: ['message'],
  connecting: ['talk'],
  recalling_dream: ['talk'],
  analyzing_dream: ['message'],
  transcribing: ['message', 'dream'],
  listening: ['message', 'talk'],
};

export const activityLabel = (key, lang = 'en') => {
  const entry = ACTIVITY_COPY[key] || ACTIVITY_COPY.default;
  return lang === 'es' ? entry.es : entry.en;
};

export const isActivityAllowed = (key, surface) =>
  (ACTIVITY_SURFACES[key] || []).includes(surface);
